import { dataService } from '@/services/dataService';
import { api } from '@/services/apiClient';
import { DATA_MODE } from '@/config/dataMode';
import { getStatusMeta } from '@/config/statuses.config';
import { LocalCommandResult, LocalCommandRoutingStep } from '@/types/orchestrator';

/**
 * LOCAL COMMAND ROUTER — Phase D §4. A small, deterministic, typed
 * registry: id/title/aliases/description/handler. NOT an AI — every
 * handler here just queries the existing repository layer (dataService)
 * for a real local answer. Unrecognized input returns kind:'unknown' so
 * AI Command can show "AI Provider Not Connected" instead of ever
 * fabricating a response.
 *
 * Architecture (§23):
 *   Ugarits Command → Local Command Router → <entity> Data Service → Local Preview Result
 */
const TODAY = '2026-08-19'; // matches the fixed reference date used by Overview/FollowUpsPage — see Known Issues

export interface CommandDefinition {
  id: string;
  titleKey: string;
  descriptionKey: string;
  aliases: string[];
  requiredData: string[];
  handler: () => Promise<LocalCommandResult>;
}

function routing(...labelKeys: string[]): LocalCommandRoutingStep[] {
  return labelKeys.map((labelKey) => ({ labelKey }));
}

const LOCAL_ROUTING = routing('commandRouter.route.command', 'commandRouter.route.router');

export const COMMAND_REGISTRY: CommandDefinition[] = [
  {
    id: 'pending-approvals',
    titleKey: 'commandRouter.commands.pendingApprovals.title',
    descriptionKey: 'commandRouter.commands.pendingApprovals.desc',
    aliases: ['pending approvals', 'show pending approvals', 'approvals'],
    requiredData: ['approvals'],
    handler: async () => {
      const approvals = (await dataService.listApprovals()).filter((a) => a.status === 'pending');
      return {
        kind: 'local_preview',
        commandId: 'pending-approvals',
        titleKey: 'commandRouter.commands.pendingApprovals.title',
        rows: approvals.map((a) => ({ label: a.actionTitle, value: getStatusMeta('approval', a.status).labelKey })),
        emptyKey: 'commandRouter.empty.noPendingApprovals',
        route: '/approvals',
        routing: [...LOCAL_ROUTING, { labelKey: 'commandRouter.route.approvalService' }],
      };
    },
  },
  {
    id: 'creators-at-risk',
    titleKey: 'commandRouter.commands.creatorsAtRisk.title',
    descriptionKey: 'commandRouter.commands.creatorsAtRisk.desc',
    aliases: ['creators at risk', 'show creators at risk', 'at risk creators', 'at-risk creators'],
    requiredData: ['creators'],
    handler: async () => {
      const creators = (await dataService.listCreators()).filter((c) => c.riskLevel === 'high');
      return {
        kind: 'local_preview',
        commandId: 'creators-at-risk',
        titleKey: 'commandRouter.commands.creatorsAtRisk.title',
        rows: creators.map((c) => ({ label: c.fullName, value: c.id })),
        emptyKey: 'commandRouter.empty.noAtRiskCreators',
        route: '/creators',
        routing: [...LOCAL_ROUTING, { labelKey: 'commandRouter.route.creatorService' }],
      };
    },
  },
  {
    id: 'overdue-followups',
    titleKey: 'commandRouter.commands.overdueFollowups.title',
    descriptionKey: 'commandRouter.commands.overdueFollowups.desc',
    aliases: ['overdue followups', 'show overdue follow-ups', 'overdue follow-ups'],
    requiredData: ['followups'],
    handler: async () => {
      const followups = (await dataService.listFollowups()).filter((f) => f.status === 'open' && f.dueDate < TODAY);
      return {
        kind: 'local_preview',
        commandId: 'overdue-followups',
        titleKey: 'commandRouter.commands.overdueFollowups.title',
        rows: followups.map((f) => ({ label: `${f.entityLabel} — ${f.title}`, value: f.dueDate })),
        emptyKey: 'commandRouter.empty.noOverdueFollowups',
        route: '/followups',
        routing: [...LOCAL_ROUTING, { labelKey: 'commandRouter.route.followupService' }],
      };
    },
  },
  {
    id: 'expiring-contracts',
    titleKey: 'commandRouter.commands.expiringContracts.title',
    descriptionKey: 'commandRouter.commands.expiringContracts.desc',
    aliases: ['expiring contracts', 'show expiring contracts'],
    requiredData: ['contracts', 'settings'],
    handler: async () => {
      const [contracts, settings] = await Promise.all([dataService.listContracts(), dataService.getSettings()]);
      const windowDays = settings.contractRules.expiryWarningDays;
      const inWindow = contracts.filter((c) => {
        if (!c.expiryDate) return false;
        const days = Math.round((new Date(c.expiryDate).getTime() - new Date(TODAY).getTime()) / 86400000);
        return days >= 0 && days <= windowDays;
      });
      return {
        kind: 'local_preview',
        commandId: 'expiring-contracts',
        titleKey: 'commandRouter.commands.expiringContracts.title',
        rows: inWindow.map((c) => ({ label: c.title, value: c.expiryDate ?? '—' })),
        emptyKey: 'commandRouter.empty.noExpiringContracts',
        route: '/contracts',
        routing: [...LOCAL_ROUTING, { labelKey: 'commandRouter.route.contractService' }],
      };
    },
  },
  {
    id: 'recent-activity',
    titleKey: 'commandRouter.commands.recentActivity.title',
    descriptionKey: 'commandRouter.commands.recentActivity.desc',
    aliases: ['recent activity', 'summarize agency activity', 'activity'],
    requiredData: ['activity'],
    handler: async () => {
      const activity = (await dataService.listActivity()).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)).slice(0, 8);
      return {
        kind: 'local_preview',
        commandId: 'recent-activity',
        titleKey: 'commandRouter.commands.recentActivity.title',
        rows: activity.map((a) => ({ label: `${a.actorLabel} — ${a.action}`, value: a.result })),
        emptyKey: 'commandRouter.empty.noActivity',
        route: '/activity',
        routing: [...LOCAL_ROUTING, { labelKey: 'commandRouter.route.activityService' }],
      };
    },
  },
  {
    id: 'connector-problems',
    titleKey: 'commandRouter.commands.connectorProblems.title',
    descriptionKey: 'commandRouter.commands.connectorProblems.desc',
    aliases: ['connector problems', 'show connector problems'],
    requiredData: ['connectors'],
    handler: async () => {
      const connectors = (await dataService.listConnectorStatuses()).filter(
        (c) => getStatusMeta('connector', c.status).tone === 'crit' || getStatusMeta('connector', c.status).tone === 'warn'
      );
      return {
        kind: 'local_preview',
        commandId: 'connector-problems',
        titleKey: 'commandRouter.commands.connectorProblems.title',
        rows: connectors.map((c) => ({ label: c.displayName, value: getStatusMeta('connector', c.status).labelKey })),
        emptyKey: 'commandRouter.empty.noConnectorProblems',
        route: '/connectors',
        routing: [...LOCAL_ROUTING, { labelKey: 'commandRouter.route.connectorService' }],
      };
    },
  },
  {
    id: 'system-status',
    titleKey: 'commandRouter.commands.systemStatus.title',
    descriptionKey: 'commandRouter.commands.systemStatus.desc',
    aliases: ['system status', 'run system diagnostics', 'run diagnostics'],
    requiredData: ['systemServices'],
    handler: async () => {
      const services = await dataService.listSystemServices();
      return {
        kind: 'local_preview',
        commandId: 'system-status',
        titleKey: 'commandRouter.commands.systemStatus.title',
        rows: services.map((s) => ({ label: s.displayName, value: getStatusMeta('system', s.status).labelKey })),
        emptyKey: 'commandRouter.empty.noSystemServices',
        route: '/system',
        routing: [...LOCAL_ROUTING, { labelKey: 'commandRouter.route.systemService' }],
      };
    },
  },
];

function normalize(input: string): string {
  return input.trim().toLowerCase();
}

/** Matches free-typed input against the registry's aliases (exact or
 * substring match) — deliberately simple, not NLP/fuzzy matching, so
 * behavior stays predictable and auditable. */
export function matchCommand(input: string): CommandDefinition | undefined {
  const q = normalize(input);
  if (!q) return undefined;
  return (
    COMMAND_REGISTRY.find((c) => c.aliases.some((a) => normalize(a) === q)) ??
    COMMAND_REGISTRY.find((c) => c.aliases.some((a) => q.includes(normalize(a)) || normalize(a).includes(q)))
  );
}

export async function runCommand(input: string): Promise<LocalCommandResult> {
  const command = matchCommand(input);
  await dataService.logLocalCommand(input, !!command);
  if (command) return command.handler();

  const routing = [...LOCAL_ROUTING, { labelKey: 'commandRouter.route.unmatched' }];

  if (DATA_MODE !== 'live') return { kind: 'unknown', routing };

  try {
    const ai = await api.post<any>('/api/ai/command', { text: input, agentKey: 'creator_manager' });

    if (ai.status === 'generated' && ai.text) {
      return { kind: 'ai_generated', text: ai.text, provider: ai.provider, model: ai.model, routing };
    }

    if (ai.status === 'not_configured') {
      return { kind: 'not_connected', provider: ai.provider, error: ai.error, routing };
    }

    return { kind: 'ai_error', provider: ai.provider, model: ai.model, error: ai.error ?? 'AI request failed', routing };
  } catch (err) {
    return { kind: 'ai_error', error: err instanceof Error ? err.message : 'AI request failed', routing };
  }
}
